import { Box, Flex, Grid } from '@tempmee/react';
import { OutlineWarning01Icon, OutlineXCircleErrorIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ConfirmPopup } from '~/commonComponents';
import { JobPreviewCard } from '~/commonComponents/JobPreviewCard';
import { EnforceUpdateInterviewRequest } from '~/constants/ff.posthog';
import { useAppSelector } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { fireInterviewRequestAddInterviewsClicked } from '~/posthogs/events';
import { parseDateString } from '~/utils/DateUtils';
import { AddMoreOnsiteInterviewButton } from '../AddMoreOnsiteInterviewButton';
import { useDeleteOnsiteInterview } from './useDeleteOnsiteInterview';

interface IOnsiteInterviewPreviewerProps {
  jobPostingId: string;
}

export const OnsiteInterviewPreviewer = ({ jobPostingId }: IOnsiteInterviewPreviewerProps) => {
  const navigate = useNavigate();
  const isInterviewRequestRedesign = useFeatureFlag(EnforceUpdateInterviewRequest);

  const interviews = useAppSelector((state) => {
    if (!jobPostingId) return [];
    return state.permanentJobs.onsiteInterviews[jobPostingId] ?? [];
  });

  const fmtOrderedInterviews = useMemo(
    () => [...interviews].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [interviews],
  );

  const {
    isConfirmOpen,
    pendingDeleteId,
    onDelete: handleDeleteClick,
    onConfirmDelete,
    onCancelDelete,
    isWarningOpen,
    onCloseWarning,
  } = useDeleteOnsiteInterview({ jobPostingId });

  const pendingDeleteInterview = useMemo(
    () => fmtOrderedInterviews.find((it) => it.id === pendingDeleteId),
    [fmtOrderedInterviews, pendingDeleteId],
  );

  const deleteConfirmDescription = pendingDeleteInterview ? (
    <>
      Are you sure you would like to delete the interview on{' '}
      <strong>{format(parseDateString(pendingDeleteInterview.date), 'EEE, MMM dd, yyyy')}</strong>?
    </>
  ) : (
    'Are you sure you would like to delete this interview?'
  );

  const handleEditClick = (id: string) =>
    navigate(`/permanent-jobs/postings/${jobPostingId}/onsite-interviews/${id}/edit`);

  if (!jobPostingId) return null;

  return (
    <>
      <Box style={{ width: 892, marginInline: 'auto' }}>
        <Flex justify="end" align="center">
          <AddMoreOnsiteInterviewButton
            includeLocationState
            jobPostingId={jobPostingId}
            variant="lite"
            onTrack={() => {
              fireInterviewRequestAddInterviewsClicked({ postingId: jobPostingId });
            }}
          />
        </Flex>
        <Grid columns={2} gapX={4} gapY={4} style={{ marginTop: 24 }}>
          {fmtOrderedInterviews.map((it) => (
            <JobPreviewCard
              breakTime={it.breakTime || ''}
              date={new Date(it.date)}
              endsAt={it.hoursDeparture}
              hourlyRate={it.hourlyRate}
              isAssistedHygiene={it.isAssistedHygiene}
              isBreakTimePaid={it.isPaidBreakTime}
              isHideTips
              isHideMealBreak={isInterviewRequestRedesign}
              isHideAssistedHygiene={isInterviewRequestRedesign}
              key={it.id}
              profession={it.profession}
              subProfession={it.subProfession}
              specialty={it.specialty}
              startsAt={it.hoursArrival}
              testName="onsite-interviews.jobs"
              onDelete={() => handleDeleteClick(it.id)}
              onEdit={() => handleEditClick(it.id)}
            />
          ))}
        </Grid>
      </Box>

      {isConfirmOpen && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <OutlineXCircleErrorIcon size={80} color={colors.primary[500]} />}
          title="Delete Interview"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          description={deleteConfirmDescription}
          descriptionStyle={{ marginBottom: 40 }}
          leftButtonText="Go Back"
          leftButtonAction={onCancelDelete}
          rightButtonText="Confirm"
          rightButtonAction={onConfirmDelete}
        />
      )}

      {isWarningOpen && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <Box style={{ marginBottom: 20 }}>
              <OutlineWarning01Icon width={80} height={80} color={colors.warning[500]} />
            </Box>
          )}
          title="Onsite Interview Needed to Post Job"
          description="You must keep at least one onsite interview to post this job. Adding more interviews gives you even better chances to fill the position successfully."
          rightButtonText="Close"
          rightButtonAction={onCloseWarning}
        />
      )}
    </>
  );
};
